import { useState, useEffect } from 'react'
import Layout from '../../components/layout/Layout'
import { useAuthStore } from '../../store/authStore'
import back_app from '../../lib/back_app'

import xdate from '../../../../../util/date.js'

type BrandAsset = {
  id: string
  brand_id: string
  asset_type: 'logo' | 'product' | 'other'
  asset_url: string
  asset_name: string
  thumbnail_url: string | null
  ts?: {insert: Date, modified: Date}
}

export default function Assets() {
  const { user } = useAuthStore()
  const [assets, setAssets] = useState<BrandAsset[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [assetType, setAssetType] = useState<'logo' | 'product' | 'other'>('logo')
  const [assetName, setAssetName] = useState('')
  const [assetFile, setAssetFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string>('')

  useEffect(() => {
    loadAssets()
  }, [user])

  const loadAssets = async () => {
    if (!user) return
    setLoading(true)
    let res = await back_app.basset_list()
    if (!res.err)
      setAssets(res.assets || [])
    setLoading(false)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    let file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      setAssetFile(file)
      setPreview(URL.createObjectURL(file))
    }
  }

  const handleUpload = async () => {
    if (!assetFile || !assetName || !user) return

    setUploading(true)

    try {
      let form_data = new FormData()
      form_data.append('file', assetFile)
      form_data.append('asset_type', assetType)
      form_data.append('asset_name', assetName)

      let res = await back_app.basset_insert(form_data)
      if (res.err) {
        alert('Upload failed: ' + res.err)
        return
      }

      setAssetFile(null)
      setPreview('')
      setAssetName('')
      loadAssets()
    } catch (error) {
      console.error('Upload error:', error)
      alert('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this asset?')) return
    let res = await back_app.basset_delete(id)
    if (!res.err)
      loadAssets()
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-dark mb-2">Brand Assets</h1>
          <p className="text-gray-600">Upload logos, products, and other brand materials</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <h2 className="text-xl font-bold text-dark mb-6">Upload New Asset</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-dark mb-2">Asset Type</label>
                  <select
                    value={assetType}
                    onChange={(e) => setAssetType(e.target.value as any)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-purple transition"
                  >
                    <option value="logo">Logo</option>
                    <option value="product">Product</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-dark mb-2">Asset Name</label>
                  <input
                    type="text"
                    value={assetName}
                    onChange={(e) => setAssetName(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-purple transition"
                    placeholder="Main Logo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-dark mb-2">Image</label>
                  {!assetFile ? (
                    <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-primary-purple transition bg-gray-50">
                      <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-600 text-sm">Upload Image</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </label>
                  ) : (
                    <div className="relative">
                      <img src={preview} alt="Preview" className="w-full h-48 object-contain rounded-xl bg-gray-50" />
                      <button
                        onClick={() => {
                          setAssetFile(null)
                          setPreview('')
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-red-600 transition"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleUpload}
                  disabled={uploading || !assetFile || !assetName}
                  className="w-full bg-gradient-to-r from-primary-purple to-primary-blue text-white py-3 rounded-xl font-bold hover:shadow-lg transition disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload Asset'}
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-dark mb-6">Your Assets ({assets.length})</h2>

              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="w-12 h-12 border-4 border-primary-purple border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : assets.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-dark mb-2">No assets yet</h3>
                  <p className="text-gray-600">Upload your first brand asset to get started</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {assets.map((asset) => (
                    <div key={asset.id} className="border-2 border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition">
                      <div className="aspect-video bg-gray-50 flex items-center justify-center p-4">
                        <img src={asset.asset_url} alt={asset.asset_name} className="max-h-full max-w-full object-contain" />
                      </div>
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-semibold text-dark">{asset.asset_name}</h3>
                            <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700 inline-block mt-1">
                              {asset.asset_type}
                            </span>
                          </div>
                          <button
                            onClick={() => handleDelete(asset.id)}
                            className="text-red-500 hover:text-red-700 transition"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                        <p className="text-xs text-gray-500">{asset.ts?.insert ? xdate.fmt_lbl_short(asset.ts.insert) : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}